import React, { useState, useEffect } from "react";
import {
  Container,
  CircularProgress,
  Typography,
  IconButton,
  Button,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Link,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FetchTemplateLibraryData } from "../api/contractVersionService";
import toast from "react-hot-toast";
import { ROLE } from "../utils/commonConstant";
import { DataGrid } from "@mui/x-data-grid";
import "../App.css";
import {
  CreateDocument,
  FetchDocumentData,
  UpdateDocument,
} from "../api/DocumentService";
import EditIcon from "@mui/icons-material/Edit";
import HeaderLogo from "../components/LogoHeader";
import { fetchSubcontractorTypeData } from "../utils/formUtils";

const DocumentPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    Name: "",
    Description: "",
    SubcontractorTypeId: "",
    IsMarkelApproval: true,
  });
  const [errors, setErrors] = useState({
    Name: "",
    Description: "",
    Template: "",
    SubcontractorType: "",
  });
  const { clientId } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem("Role");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    id: false,
    actions: role === ROLE.MARKEL ? false : true,
    HHTemplateName: role !== ROLE.MARKEL,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState([]);
  const [optionTemplateLibrary, setOptionTemplateLibrary] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSubcontractTypeOption, setSelectedSubcontractTypeOption] =
    useState(null);
  const [optionSubcontractorType, setOptionSubcontractorType] = useState([]);
  const fetchsubcontractorType = async () => {
    const data = await fetchSubcontractorTypeData(clientId);
    const newOptionSubcontractorType = [...data, { Id: null, Name: "ALL" }];
    setOptionSubcontractorType(newOptionSubcontractorType);
  };
  const fetchTemplateLibrary = async () => {
    const data = await FetchTemplateLibraryData();
    setOptionTemplateLibrary(data);
  };
  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
      headerClassName: "bold-header",
    },

    {
      field: "Name",
      headerName: "Document Name",
      width: 300,
      align: "center",
      headerAlign: "center",
      headerClassName: "bold-header",
      renderCell: (params) => {
        const href =
          role === ROLE.MARKEL
            ? `/contract/${params.row.ClientId}/${params.row.Id}`
            : `/${params.row.ClientId}/${params.row.Id}/${params.row.HHTemplateId}`;

        return (
          <Link
            href={href}
            underline="hover"
            sx={{
              color: "text.primary",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            {params.value}
          </Link>
        );
      },
    },
    {
      field: "SubcontractorType",
      headerName: "Subcontractor Type",
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "bold-header",
    },
    {
      field: "HHTemplateName",
      headerName: "Template",
      align: "center",
      headerAlign: "center",
      headerClassName: "bold-header",
      width: 200,
    },
    {
      field: "IsMarkelApproval",
      headerName: "Approval Required",
      align: "center",
      headerAlign: "center",
      headerClassName: "bold-header",
      width: 200,
      renderCell: (params) =>
        params.row.IsMarkelApproval ? (
          <CheckIcon color="success" />
        ) : (
          <CloseIcon color="error" />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      align: "center",
      headerClassName: "bold-header",
      headerAlign: "center",
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit" arrow>
            <IconButton
              onClick={(event) => handleEdit(event, params.row)}
              color="primary"
              disabled={role === ROLE.MARKEL}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
  const fetchData = async () => {
    try {
      const result = await FetchDocumentData(clientId, role);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  const addIdToData = (data) => {
    // Create a new array with 'id' field added to each object
    return data.map((item) => ({ ...item, id: item.Id }));
  };

  useEffect(() => {
    fetchData();
    fetchTemplateLibrary();
    fetchsubcontractorType();
  }, [clientId]);

  useEffect(() => {
    const updatedRows = addIdToData(data);
    setRows(updatedRows);
  }, [data]);

  if (loading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">
          Error fetching data: {error.message}
        </Typography>
      </Container>
    );
  }

  const handleEdit = (e, row) => {
    console.log(row, "row");
    setFormData(row);
    setSelectedOption({
      Id: row.HHTemplateId,
      Name: row.HHTemplateName,
    });
    setSelectedSubcontractTypeOption({
      Id: row?.SubContractorTypeId,
      Name: row?.SubcontractorType,
    });
    setEditDialogOpen(true);
    setIsEditMode(true);
    console.log(formData);
  };

  const handleCreate = () => {
    setEditDialogOpen(true);
    setIsEditMode(false);
  };

  const handleTour = () => {
    navigate(`/tutorial`);
  };

  const handleHome = () => {
    navigate(`/home`);
  };
  const handleTemplate = () => {
    navigate(`/${clientId}/template`);
  };

  const handleEditDialogClose = () => {
    setErrors({
      Name: "",
      Description: "",
      Template: "",
      SubcontractorType: "",
    });
    setFormData({
      Name: "",
      Description: "",
      SubcontractorTypeId: "",
      IsMarkelApproval: true,
    });
    setSelectedSubcontractTypeOption(null);
    setSelectedOption(null);
    setEditDialogOpen(false);
  };
  const handleAutocompleteChange = (event, newValue) => {
    setErrors({ ...errors, SubcontractorType: "" });
    setSelectedSubcontractTypeOption(newValue);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    console.log(name, value, type, checked);
    const updatedValue = type === "checkbox" ? checked : value;
    setFormData({ ...formData, [name]: updatedValue });
    setErrors({ ...errors, [name]: "" });
  };
  const handleAutocompleteTemplateChange = (event, newValue) => {
    setErrors({ ...errors, template: "" });
    setSelectedOption(newValue);
  };

  const handleSave = async () => {
    let formValid = true;
    const newErrors = { ...errors };
    if (!formData.Name) {
      newErrors.Name = "Document Name is required";
      formValid = false;
    }
    if (!selectedOption) {
      newErrors.template = "Template is required";
      formValid = false;
    }
    if (!selectedSubcontractTypeOption) {
      newErrors.SubcontractorType = "Subcontractor Type is required";
      formValid = false;
    }
    if (!formValid) {
      setErrors(newErrors);
      return;
    }
    try {
      console.log("Save Edit Data:", formData);
      if (!isEditMode) {
        const response = await CreateDocument({
          Name: formData.Name,
          Description: formData.Description,
          ClientId: clientId,
          hhTemplateId: selectedOption.Id,
          SubContractorTypeId: selectedSubcontractTypeOption?.Id,
          IsMarkelApproval: formData.IsMarkelApproval,
        });
        if (response.ok) {
          toast.success(
            isEditMode ? "Updated successfully." : "Created successfully."
          );
        }
      } else {
        const response = await UpdateDocument({
          Id: formData.Id,
          Name: formData.Name,
          Description: formData.Description,
          ClientId: clientId,
          hhTemplateId: selectedOption.Id,
          subContractorTypeId: selectedSubcontractTypeOption?.Id,
          isMarkelApproval: formData.IsMarkelApproval,
        });
        if (response.ok) {
          toast.success(
            isEditMode ? "Updated successfully." : "Created successfully."
          );
        }
      }

      fetchData();
    } catch (error) {
      // Handle error
    }
    setEditDialogOpen(false);
  };

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <HeaderLogo clientId={clientId} heading={"Document Library"} />
        <Container>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            mb={2}
            spacing={2}
          >
            {role != ROLE.MARKEL && (
              <>
                <Grid item justifyContent="flex-end">
                  <Tooltip title="Start the interactive tutorial to learn how to fill out the Client Sign document.">
                    <Button
                      variant="contained"
                      color="inherit"
                      onClick={handleTour}
                    >
                      Watch Tutorial
                    </Button>
                  </Tooltip>
                </Grid>
                <Grid item justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                  >
                    Create New
                  </Button>
                </Grid>
                <Grid item justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTemplate}
                  >
                    Template
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 50, 100]}
            disableSelectionOnClick // Disable row selection
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
          />
        </Container>
      </Box>
      {/* model popup for creat/edit */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="md"
        fullWidth={fullScreen}
      >
        <DialogTitle>
          {" "}
          {isEditMode ? "Edit Document" : "Create New Document"}
        </DialogTitle>
        <DialogContent sx={{ width: fullScreen ? "100%" : "600px" }}>
          <TextField
            margin="dense"
            name="Name"
            label="Document Name"
            type="text"
            variant="outlined"
            fullWidth
            value={formData.Name}
            onChange={handleInputChange}
            error={!!errors.Name} // Show error if there's an error
            helperText={errors.Name} // Display the error messag
          />
          <Autocomplete
            disabled={isEditMode}
            options={optionTemplateLibrary}
            value={selectedOption}
            getOptionLabel={(option) => option.Name}
            isOptionEqualToValue={(option, value) =>
              option.valueOf === value.valueOf
            }
            onChange={(event, newValue) =>
              handleAutocompleteTemplateChange(event, newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Template"
                margin="dense"
                fullWidth
                error={!!errors.template}
                helperText={errors.template || ""}
                disabled={isEditMode}
              />
            )}
          />
          <Autocomplete
            options={optionSubcontractorType}
            value={selectedSubcontractTypeOption}
            getOptionLabel={(option) => option?.Name}
            // getOptionSelected={(option, value) => option.Id === value.Id}
            isOptionEqualToValue={(option, value) =>
              option?.valueOf === value?.valueOf
            }
            onChange={(event, newValue) =>
              handleAutocompleteChange(event, newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Subcontractor Type"
                margin="dense"
                fullWidth
                error={!!errors.SubcontractorType}
                helperText={errors.SubcontractorType || ""}
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.IsMarkelApproval}
                onChange={handleInputChange}
                name="IsMarkelApproval"
              />
            }
            label="Markel Approval Required"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentPage;
